import { create } from 'zustand';
import apiClient from '../api/client';
import { applyLocale, DEFAULT_LOCALE, isLocale, LOCALE_STORAGE_KEY, type Locale } from '../i18n';

interface LocaleStore {
  locale: Locale;
  /** True while a change is being written back to the user row. */
  saving: boolean;
  /** User-initiated switch: applies immediately, then persists to users.locale. */
  setLocale: (locale: Locale) => Promise<void>;
  /** Server-initiated: adopt the locale carried by /me/capabilities on boot. No write-back. */
  adopt: (locale: unknown) => void;
}

function cached(): Locale {
  if (typeof localStorage === 'undefined') return DEFAULT_LOCALE;
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  return isLocale(stored) ? stored : DEFAULT_LOCALE;
}

/**
 * The signed-in user's interface language.
 *
 * Two stores of truth, deliberately: `users.locale` in the database is authoritative — a
 * preference belongs to the person, so it follows them to any terminal they sign in on — while
 * localStorage holds a cache so a reload paints in the right language before the boot request
 * has answered. `adopt()` reconciles the two when /me/capabilities lands; the server always wins.
 *
 * This is the language of the SCREEN only. Documents are French regardless — see src/i18n.
 */
export const useLocaleStore = create<LocaleStore>((set, get) => ({
  locale: cached(),
  saving: false,

  async setLocale(locale) {
    if (get().locale === locale) return;
    // Applied before the round-trip: the switch must feel instant, and a failed save leaves the
    // user looking at the language they asked for with the cache agreeing — the next sign-in on
    // another machine simply won't have it.
    applyLocale(locale);
    set({ locale, saving: true });
    try {
      await apiClient.patch('/me/profile', { locale });
    } finally {
      set({ saving: false });
    }
  },

  adopt(locale) {
    if (!isLocale(locale)) return;
    if (get().locale === locale) return;
    applyLocale(locale);
    set({ locale });
  },
}));
